import { useEffect, useRef, useCallback, useMemo } from 'react';

import { useLocation, useNavigate } from 'react-router';
type RouteEvent = CustomEvent<{
  pathname: string;
  search: string;
  hash: string;
}>;

const useSyncAppRouter = (basePaths: string[]) => {
  const navigate = useNavigate();
  const location = useLocation();
  const previousLocationRef = useRef<string>('');

  // Memoizar a string completa da location para evitar recomputações desnecessárias
  const currentLocationString = useMemo(() => {
    return `${location.pathname}${location.search}${location.hash}`;
  }, [location.pathname, location.search, location.hash]);

  // Função para verificar se o pathname corresponde a algum dos basePaths
  const isPathInBasePaths = useCallback(
    (pathname: string) => {
      return basePaths.some((basePath) => pathname.startsWith(basePath));
    },
    [basePaths],
  );

  // Memoizar o event handler para evitar recriação desnecessária
  const appNavigated = useCallback(
    ({ detail: { pathname, search, hash } }: RouteEvent) => {
      // Verifica se o pathname está nos basePaths antes de navegar
      if (!isPathInBasePaths(pathname)) {
        return;
      }

      const newLocationString = `${pathname}${search}${hash}`;
      if (newLocationString === currentLocationString) {
        return;
      }
      navigate(newLocationString);
    },
    [isPathInBasePaths, currentLocationString, navigate],
  );

  useEffect(() => {
    window.addEventListener('app', appNavigated as EventListener);
    return () => {
      window.removeEventListener('app', appNavigated as EventListener);
    };
  }, [appNavigated]);

  useEffect(() => {
    // Só dispara o evento se a localização realmente mudou
    if (previousLocationRef.current !== currentLocationString) {
      previousLocationRef.current = currentLocationString;

      window.dispatchEvent(
        new CustomEvent('host', {
          detail: { pathname: location.pathname, search: location.search, hash: location.hash },
        }),
      );
    }
  }, [currentLocationString, location.pathname, location.search, location.hash]);
};
export { useSyncAppRouter };
